import React, { useEffect, useState } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { Button, CircularProgress, IconButton, Stack } from '@mui/material'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Box } from '@mui/system'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SettingsBox } from 'src/Elements/Data'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import { useOwnerConfigurationFormInitializer } from 'src/Libs/installations/hooks/use-owner-configuration-form-initializer'
import { zodResolver } from '@hookform/resolvers/zod'
import { Installation, OwnerConfigurationFormModel } from 'src/Libs/installations/models/installation.model'
import { TFunction } from 'i18next'
import { useInstallationInvitation } from 'src/Libs/installation-rights-management/hooks'
import OwnerInvitationStatus from './OwnerInvitationStatus'
import useModals from 'src/Hooks/useModals'
import OwnerDisplay from './OwnerDisplay'
import { useFetchUserData } from 'src/Hooks/useFetchUserData'
import { UserContactInfo } from 'src/State/User/UserModel'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { J2Delete } from '@e3dc-react/icons'
import { useDeleteAccountLink } from 'src/Libs/installation-details/hooks'
import { LoadingOverlay } from 'src/Elements/Overlays'

interface Props {
  installationLoading: boolean
  installationId?: number
  installation?: Installation
  refreshInstallation?: (installationId: string) => Promise<void>
}

const CompanyFormComponent: React.FC<{
  t: TFunction
  disabled?: boolean
  installationId?: number
  ownerAccountLinkId?: number
  ownerInvitationToken?: string
  removeInvitation: (token: string, title?: string, text?: string) => Promise<void>
  onDeleteStateChange: (isDeleting: boolean) => void
  onDeleteSuccess: () => Promise<void>
  hasEmailChanged?: boolean
}> = ({
  t,
  disabled,
  installationId,
  ownerAccountLinkId,
  ownerInvitationToken,
  removeInvitation,
  onDeleteStateChange,
  onDeleteSuccess,
  hasEmailChanged,
}) => {
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { openConfirmationModal } = useModals()
  const { mutateAsync: deleteAccountLink, isPending: deleteAccountLinkLoading } = useDeleteAccountLink({
    onError: () => enqueueErrorStack(t('installation-configuration.owner-settings.remove-owner.error')),
    onSuccess: () => enqueueSuccessStack(t('installation-configuration.owner-settings.remove-owner.success')),
  })
  const isReinvite = Boolean(ownerAccountLinkId) || Boolean(ownerInvitationToken)

  const onDelete = async (): Promise<void> => {
    if (ownerAccountLinkId && installationId) {
      openConfirmationModal({
        title: t('installation-configuration.owner-settings.remove-owner.title'),
        text: t('installation-configuration.owner-settings.remove-owner.text'),
        onConfirm: async () => {
          onDeleteStateChange(true)

          try {
            await deleteAccountLink({ installationId, accountId: ownerAccountLinkId })
            await onDeleteSuccess()
          } finally {
            onDeleteStateChange(false)
          }
        },
        buttonText: [t('common.cancel'), t('common.confirm')],
        onCancel: () => onDeleteStateChange(false),
      })

      return
    }

    if (ownerInvitationToken) {
      onDeleteStateChange(true)

      try {
        await removeInvitation(
          ownerInvitationToken,
          t('installation-configuration.owner-settings.remove-owner-invitation.title'),
          t('installation-configuration.owner-settings.remove-owner-invitation.text')
        )
        await onDeleteSuccess()
      } finally {
        onDeleteStateChange(false)
      }
    }
  }

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} sx={{ width: '100%' }}>
      <Stack direction={'row'} gap={2} sx={{ width: '100%' }}>
        <TextField name={'email'} label={t('common.email')} disabled={disabled} fullWidth />
        {isReinvite && (
          <IconButton sx={{ color: 'grey.grey00', padding: 0 }} onClick={onDelete} disabled={disabled || deleteAccountLinkLoading}>
            <J2Delete />
          </IconButton>
        )}
      </Stack>
      <Button variant={'contained'} type={'submit'} disabled={disabled} sx={{ flexShrink: 0 }}>
        {isReinvite && !hasEmailChanged ? t('installation-configuration.owner-settings.invite-again') : t('common.invite')}
      </Button>
    </Stack>
  )
}

const OwnerConfiguration: React.FC<Props> = ({ installationLoading, installationId, installation, refreshInstallation }) => {
  const { t } = useCustomTranslation()
  const { inviteUser, getInvitations, resendInvitation, removeInvitation, isRemoveLoading } = useInstallationInvitation()
  const { data: invitations, isLoading: invitationsLoading, refetch: refetchInvitations } = getInvitations(installationId)
  const { openConfirmationModal } = useModals()
  const { fetchUserContactInfo } = useFetchUserData()
  const [userData, setUserData] = useState<UserContactInfo>()
  const [isDeleting, setIsDeleting] = useState(false)
  const { enqueueErrorStack } = useSnackbar()

  useEffect(() => {
    if (installation?.ownerAccountId) {
      fetchUserContactInfo(Number.parseInt(installation.ownerAccountId))
        .then(userData => {
          setUserData(userData)
        })
        .catch(e => {
          console.error('Failed to fetch owner user data', e)
          enqueueErrorStack(t('installation-configuration.owner-settings.owner-fetch-error'))
        })
    } else {
      setUserData(undefined)
    }
  }, [installation?.ownerAccountId]) //eslint-disable-line react-hooks/exhaustive-deps

  const ownerInvitation = invitations?.find(invitation => invitation.inviteType === 'handover-planned' && invitation.deletedAt === null)

  const { initialValues, validationSchema } = useOwnerConfigurationFormInitializer(ownerInvitation, userData)

  const methods = useForm<OwnerConfigurationFormModel>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })

  useEffect(() => {
    methods.reset(initialValues)
  }, [initialValues, methods])

  const email = useWatch({ name: 'email', control: methods.control })

  const updateData = async (installationId?: string): Promise<void> => {
    if (installationId) {
      await refreshInstallation?.(installationId)
    }
    await refetchInvitations()
  }

  const handleInvite = (): void => {
    if (!installationId || !email) return

    inviteUser(installationId, email, false, true).then(async () => {
      await updateData(installationId.toString())
    })
  }

  const onSubmit = (): void => {
    if (!email || !installationId) return

    if (ownerInvitation?.accountId === email) {
      resendInvitation(installationId, ownerInvitation.token).then(() => refetchInvitations())
    } else if (installation?.ownerAccountId) {
      openConfirmationModal({
        title: t('installation-configuration.owner-settings.change-existing-owner-confirmation.title'),
        text: t('installation-configuration.owner-settings.change-existing-owner-confirmation.description'),
        buttonText: [t('common.cancel'), t('installation-configuration.owner-settings.change-existing-owner-confirmation.confirm-button')],
        onConfirm: handleInvite,
      })
    } else if (ownerInvitation && ownerInvitation.accountId !== email) {
      openConfirmationModal({
        title: t('installation-configuration.owner-settings.change-owner-confirmation.title'),
        text: t('installation-configuration.owner-settings.change-owner-confirmation.description'),
        buttonText: [t('common.cancel'), t('installation-configuration.owner-settings.change-owner-confirmation.confirm-button')],
        onConfirm: handleInvite,
      })
    } else {
      handleInvite()
    }
  }

  return (
    <AccordionItem
      title={t('installation-configuration.owner-settings.title')}
      expanded
      accordionDetailsSx={{ px: 4, py: 2 }}
      headerChildren={<OwnerInvitationStatus invitationLoading={invitationsLoading} invitation={ownerInvitation} installation={installation} />}
    >
      <Stack justifyContent={'center'} alignItems={'center'} sx={{ position: 'relative' }}>
        {installationLoading ? (
          <CircularProgress />
        ) : (
          <FormProvider {...methods}>
            <Box sx={{ width: '100%' }}>
              {(isDeleting || isRemoveLoading) && <LoadingOverlay />}
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <SettingsBox
                  gridSizes={{ smLeft: 12, smRight: 12 }}
                  title={t('installation-configuration.owner-settings.email')}
                  suffix={
                    <CompanyFormComponent
                      t={t}
                      installationId={installationId}
                      ownerAccountLinkId={installation?.ownerId}
                      ownerInvitationToken={ownerInvitation?.token}
                      removeInvitation={removeInvitation}
                      onDeleteStateChange={setIsDeleting}
                      onDeleteSuccess={() => updateData(installationId?.toString())}
                      disabled={invitationsLoading}
                      hasEmailChanged={email !== initialValues.email}
                    />
                  }
                />
              </form>
            </Box>
          </FormProvider>
        )}
        {installation?.ownerAccountId && <OwnerDisplay userData={userData} userId={Number.parseInt(installation.ownerAccountId)} />}
      </Stack>
    </AccordionItem>
  )
}

export default OwnerConfiguration
